import { useEffect, useRef, useState } from 'react';

import { DEFAULT_COUNTDOWN_SECONDS } from '@/constants';

type UseCountdownOptions = {
  /**
   * @default true
   */
  enabled?: boolean;

  onFinish?: () => void;

  /**
   * @default DEFAULT_COUNTDOWN_SECONDS
   */
  seconds?: number;
};

export function useCountdown({ enabled = true, onFinish, seconds = DEFAULT_COUNTDOWN_SECONDS }: UseCountdownOptions) {
  const [countdown, setCountdown] = useState<number>(seconds);
  const intervalRef = useRef<NodeJS.Timeout>();
  const onFinishRef = useRef(onFinish);

  useEffect(() => {
    onFinishRef.current = onFinish;
  });

  function stopCountdown() {
    clearInterval(intervalRef.current);
  }

  useEffect(() => {
    setCountdown(seconds);

    if (!enabled) {
      return;
    }

    function onFinishCountdown() {
      stopCountdown();
      onFinishRef.current?.();
    }

    function startCountdown(intervalStepMs: number = 1000) {
      intervalRef.current = setInterval(() => {
        setCountdown((currentValue) => {
          const newValue = currentValue - 1;

          if (newValue <= 0) {
            onFinishCountdown();
          } else if (newValue < 1) {
            // when left a fraction of a second
            // we don't want to wait a whole second

            stopCountdown();
            startCountdown(newValue * 1000);
          }

          return newValue;
        });
      }, intervalStepMs);
    }

    startCountdown();

    return () => {
      stopCountdown();
    };
  }, [enabled, seconds]);

  return { countdown: enabled ? countdown : undefined };
}
